<i18n lang="yaml">
ru:
  inn_any_country: 'Налоговый номер в стране регистрации'
  register_button: 'Зарегистрироваться'
en:
  inn_any_country: 'Tax number'
  register_button: 'Register'
</i18n>

<template>
  <form @submit.prevent="submitForm">
    <div class="form">
      <div class="field-country-tel" :class="{'error': errors.phone && errors.phone.length}">
        <v-select
          v-model="userData.country"
          :options="countries.map(x => ({id: x.id, label: x.alpha}))"
          :reduce="x => x.id"
          :searchable="false"
          :clearable="false"
        >
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><i class="svg-icon arrow" /i></span>
          </template>
        </v-select>
        <div class="field">
          <label class="error-label" v-if="errors.phone && errors.phone.length && !userData.phone">{{ errors.phone[0] }}</label>
          <input-phone
            v-model="userData.phone"
            :value="userData.phone"
            :mask="phoneMask"
            :text="$t('country_phone')" 
            @clear-errors="clearErrors('phone')"
          />
          <label v-if="errors.phone && errors.phone.length && userData.phone">{{ errors.phone[0] }}</label>
          <label v-else-if="errors.phone && errors.phone.length && !userData.phone">{{ $t('country_phone') }}</label>
          <label v-else>{{ $t('country_phone') }}</label>
        </div>
      </div>

      <field-text
        v-model="userData.inn"
        :value="userData.inn"
        :text="!selectedCountry || selectedCountry.inn_check ? $t('inn') : $t('inn_any_country')"
        :errors="errors.inn"
        @clear-errors="clearErrors('inn')"
      />

      <div class="field-v-select" :class="{'error': errors.company_ownership && errors.company_ownership.length, 'disabled': innCheckEnabled}">
        <label class="error-label"
               v-if="errors.company_ownership && errors.company_ownership.length && !userData.company_ownership">
          {{ errors.company_ownership[0] }}</label>
        <v-select v-model="userData.company_ownership"
                  :options="companyOwnerships.map(x => ({id: x.id, label: getCompanyOwnershipLocaleData(x).name}))"
                  :reduce="x => x.id" :searchable="false" :clearable="false"
                  @option:selected="clearErrors('company_ownership')"
                  :class="{'selected': !!userData.company_ownership, 'disabled': innCheckEnabled}">
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes"><i class="svg-icon arrow"></i></span>
          </template>
        </v-select>
        <label v-if="errors.company_ownership && errors.company_ownership.length && userData.company_ownership">{{ errors.company_ownership[0] }}</label>
        <label v-else-if="errors.company_ownership && errors.company_ownership.length && !userData.company_ownership">{{ $t('company_ownership') }}</label>
        <label v-else>{{ $t('company_ownership') }}</label>
      </div>

      <field-text v-model="userData.kpp" :value="userData.kpp" :text="$t('kpp')" :disabled="!kppRequired"
                  :errors="errors.kpp" @clear-errors="clearErrors('kpp')" />

      <field-text v-model="userData.company_name" :value="userData.company_name" :text="$t('company_name')"
                  :disabled="innCheckEnabled" :errors="errors.company_name" @clear-errors="clearErrors('company_name')" />

      <field-text v-model="userData.email" :value="userData.email" text="Email"
                  :errors="errors.email" @clear-errors="clearErrors('email')" autocomplete="UserName" />

      <field-text v-model="userData.first_name" :value="userData.first_name" :text="$t('first_name')"
                  :errors="errors.first_name" @clear-errors="clearErrors('first_name')" />

      <field-text v-model="userData.last_name" :value="userData.last_name" :text="$t('last_name')"
                  :errors="errors.last_name" @clear-errors="clearErrors('last_name')" />

      <field-password v-model="userData.password" :value="userData.password" :text="$t('set_password')"
                      :errors="errors.password" @clear-errors="clearErrors('password')" :show-icon="true"
                      :show-password="showPassword" @changeTypeField="changeTypeField($event)"
      />

      <field-password v-model="userData.confirm_password" :value="userData.confirm_password" :text="$t('confirm_password')"
                      :errors="errors.confirm_password" @clear-errors="clearErrors('confirm_password')"
                      :show-password="showPassword"
      />
    </div>
    <div class="captcha-links">
      <span class="error-captcha" v-if="errors.recaptcha && errors.recaptcha.length">
        {{ $t('captcha_error') }}
      </span>
      <site-text-block type="form_submit" class="submit-text" />
      <button 
        type="submit"
        :disabled="pendingRegister"
      >{{ $t('register_button') }}<span v-if="pendingRegister" class="svg-icon loader" /></button>
    </div>
  </form>
</template>

<script>
import vSelect from 'vue-select';
import {Country} from '@/models/country';
import {CompanyOwnership} from '@/models/company';

import FieldPassword from '@/components/controls/field-password';
import FieldText from '@/components/controls/field-text';
import InputPhone from '@/components/controls/input-phone';
import SiteTextBlock from '@/components/blocks/site-text-block';

export default {
  components: {
    FieldPassword,
    FieldText,
    InputPhone,
    SiteTextBlock,
    vSelect,
  },
  data() {
    return {
      countries: [],
      companyOwnerships: [],
      userData: {
        mode: 'distributor',
        country: null,
        phone: null,
        company_ownership: null,
        inn: null,
        kpp: null,
        company_name: null,
        first_name: null,
        last_name: null,
        email: null,
        password: null,
        confirm_password: null,
        recaptcha: null,
      },
      errors: {},
      showPassword: false,
      pendingRegister: false,
    }
  },
  watch: {
    'userData.inn'() {
      this.checkInn();
    },
    phoneMask() {
      this.userData.phone = null;
    },
    kppRequired(value) {
      if (!value) {
        this.userData.kpp = null;
        this.clearErrors('kpp');
      }
    },
    innCheckEnabled(value) {
      if (value) {
        this.userData.company_name = null;
        this.userData.company_ownership = null;
        this.checkInn();
      }
    },
  },
  computed: {
    selectedCountry() {
      return this.countries.find(x => x.id === this.userData.country);
    },
    phoneMask() {
      return this.selectedCountry ? this.selectedCountry.phone_mask : '';
    },
    selectedCompanyOwnership() {
      return this.companyOwnerships.find(x => x.id === this.userData.company_ownership);
    },
    kppRequired() {
      return !this.selectedCompanyOwnership || this.selectedCompanyOwnership.kpp_required;
    },
    innCheckEnabled() {
      return !this.selectedCountry || this.selectedCountry.inn_check;
    },
  },
  async fetch() {
    [this.countries, this.companyOwnerships] = await Promise.all([
      Country.config({count_loading: true}).get(),
      CompanyOwnership.config({count_loading: true}).get(),
    ]);
    if (this.countries.length) {
      this.userData.country = this.countries[0].id;
    }
  },
  beforeDestroy() {
    this.$recaptcha.destroy();
  },
  methods: {
    getCompanyOwnershipLocaleData(co) {
      return co.company_ownership_translations?.find(x => x.language.slug === this.$i18n.locale) || {};
    },
    clearErrors(field) {
      this.errors[field] = [];
    },
    async submitForm() {
      try {
        if(!this.pendingRegister) {
          this.pendingRegister = true;
          this.userData.recaptcha = await this.$recaptcha.execute('register');
          await this.$axios.$post('/user/registration/', this.userData);
          this.pendingRegister = false;
          this.$emit('registration-completed');
        }
      } catch (e) {
        try {
          this.errors = e.response.data;
          this.pendingRegister = false;
        } catch(e) {
          this.$notify({
            duration: 5000,
            text: this.$t('unknown_error'),
          });
          this.pendingRegister = false;
        }
      }
    },
    changeTypeField(showPassword) {
      this.showPassword = showPassword;
    },
    checkInn() {
      if (this.selectedCountry.inn_check && this.userData.inn) {
        const innMinLength = Math.min(...this.selectedCountry.country_limits.map(x => x.inn_min_length));
        const innMaxLength = Math.max(...this.selectedCountry.country_limits.map(x => x.inn_max_length));
        const inn = String(this.userData.inn).trim();
        if (inn && inn.length >= innMinLength && inn.length <= innMaxLength) {
          this.$axios.$post('/user/check-inn/', this.userData).then((response) => {
            if (response) {
              Object.keys(response).forEach((key) => {
                this.userData[key] = response[key];
                this.clearErrors(key);
              });
            }
          });
        }
      }
    },
  },
}
</script>
